/**
 * Admin ReservePay Transactions
 * Read-only view of all PromptPay (ReservePay) auto-confirmed transactions.
 * Admin does NOT need to approve these — they are auto-confirmed via webhook.
 */
import { useState } from 'react'
import {
  useAdminReservePayTransactions,
  AdminReservePayTransactionsFilters,
  verifyReservePayTransaction,
  refundReservePayTransaction,
  previewReservePayReconciliation,
  reconcileReservePayTransaction,
  CrossCheckResult,
  ReservePayReconciliationPreview,
} from '../../hooks/useAdminReservePayTransactions'
import type { AdminReservePayTransaction } from '../../hooks/useAdminReservePayTransactions'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { CheckCircle, XCircle, Clock, AlertCircle, Eye, Zap, RefreshCw, AlertTriangle, Undo2, Loader2 } from 'lucide-react'

// ─── helpers ────────────────────────────────────────────────────────────────

function formatCurrency(amount: number) {
  return new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB' }).format(amount)
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('th-TH', {
    year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

function getStatusBadge(status: string) {
  const map: Record<string, { label: string; icon: any; className: string }> = {
    created:   { label: 'รอดำเนินการ', icon: Clock,        className: 'bg-gray-500 text-white' },
    pending:   { label: 'กำลังดำเนินการ', icon: Clock,     className: 'bg-yellow-500 text-white' },
    completed: { label: 'สำเร็จ',      icon: CheckCircle,  className: 'bg-green-600 text-white' },
    failed:    { label: 'ล้มเหลว',     icon: XCircle,      className: 'bg-red-600 text-white' },
    expired:   { label: 'หมดอายุ',     icon: AlertCircle,  className: 'bg-gray-400 text-white' },
    cancelled: { label: 'ยกเลิก',      icon: XCircle,      className: 'bg-red-400 text-white' },
  }
  const cfg = map[status] ?? map.pending
  const Icon = cfg.icon
  return (
    <Badge className={cfg.className}>
      <Icon className="w-3 h-3 mr-1" />
      {cfg.label}
    </Badge>
  )
}

function getTypeBadge(type: string) {
  return type === 'package_purchase'
    ? <span className="text-blue-600 font-medium text-sm">ซื้อแพ็กเกจ</span>
    : <span className="text-green-600 font-medium text-sm">เติมเครดิต</span>
}

function getFlowLabel(tx: AdminReservePayTransaction) {
  const flow = tx.metadata?.flow
  if (flow === 'custom_ui') return 'QR In-App'
  if (flow === 'hosted_checkout') return 'Hosted Checkout'
  return flow ?? '-'
}

function getMethodLabel(tx: AdminReservePayTransaction): { label: string; color: string } {
  const scope = tx.metadata?.payment_method_scope
  switch (scope) {
    case 'promptpay':      return { label: 'PromptPay', color: 'text-green-600' }
    case 'card':            return { label: 'บัตรเครดิต', color: 'text-purple-600' }
    case 'mobile_banking':  return { label: 'Mobile Banking', color: 'text-orange-600' }
    case 'true_money':      return { label: 'TrueMoney', color: 'text-red-600' }
    case 'alipay':          return { label: 'Alipay', color: 'text-blue-600' }
    case 'wechat':          return { label: 'WeChat', color: 'text-emerald-600' }
    case 'promptpay_first': return { label: 'PromptPay', color: 'text-green-600' }
    default:                return { label: scope ?? '-', color: 'text-gray-500' }
  }
}

function getOperationalBadge(status: string | null, needsAttention: boolean) {
  if (!status) {
    return <Badge variant="outline">ยังไม่ตรวจ</Badge>
  }

  const map: Record<string, { label: string; className: string }> = {
    OK: { label: 'ปกติ', className: 'bg-green-600 text-white' },
    WEBHOOK_MISSING: { label: 'Webhook ไม่มา', className: 'bg-yellow-500 text-white' },
    SIGNATURE_INVALID: { label: 'Signature ผิด', className: 'bg-red-600 text-white' },
    STATUS_MISMATCH: { label: 'สถานะไม่ตรง', className: 'bg-orange-500 text-white' },
    PROVIDER_UNREACHABLE: { label: 'เชื่อม ReservePay ไม่ได้', className: 'bg-gray-500 text-white' },
    NEEDS_MANUAL_REVIEW: { label: 'ต้องตรวจเอง', className: 'bg-red-500 text-white' },
  }

  const cfg = map[status] || { label: status, className: needsAttention ? 'bg-red-500 text-white' : 'bg-gray-500 text-white' }
  return <Badge className={cfg.className}>{cfg.label}</Badge>
}

// ─── detail modal ────────────────────────────────────────────────────────────

function DetailModal({ tx, onClose, onChanged }: { tx: AdminReservePayTransaction; onClose: () => void; onChanged: () => void }) {
  const [verifying, setVerifying] = useState(false)
  const [crossCheck, setCrossCheck] = useState<CrossCheckResult | null>(null)
  const [verifyError, setVerifyError] = useState<string | null>(null)
  const [reconcileReason, setReconcileReason] = useState('')
  const [previewing, setPreviewing] = useState(false)
  const [reconciling, setReconciling] = useState(false)
  const [reconcilePreview, setReconcilePreview] = useState<ReservePayReconciliationPreview | null>(null)
  const [reconcileError, setReconcileError] = useState<string | null>(null)
  const [reconcileSuccess, setReconcileSuccess] = useState<string | null>(null)

  async function handleVerify() {
    setVerifying(true)
    setVerifyError(null)
    setCrossCheck(null)
    setReconcilePreview(null)
    setReconcileError(null)
    setReconcileSuccess(null)
    try {
      const result = await verifyReservePayTransaction(tx.id)
      setCrossCheck(result)
    } catch (err: unknown) {
      setVerifyError(
        err instanceof Error
          ? err.message
          : (err as any)?.response?.data?.error || 'เกิดข้อผิดพลาดระหว่างตรวจสอบ'
      )
    } finally {
      setVerifying(false)
    }
  }

  async function handlePreviewReconcile() {
    setPreviewing(true)
    setReconcileError(null)
    setReconcileSuccess(null)
    try {
      const preview = await previewReservePayReconciliation({
        transactionId: tx.id,
        checkId: crossCheck?.checkId,
        reason: reconcileReason,
      })
      setReconcilePreview(preview)
    } catch (err: unknown) {
      setReconcileError(
        err instanceof Error
          ? err.message
          : (err as any)?.response?.data?.error || 'ไม่สามารถ preview reconcile ได้'
      )
    } finally {
      setPreviewing(false)
    }
  }

  async function handleExecuteReconcile() {
    if (!reconcilePreview) return

    setReconciling(true)
    setReconcileError(null)
    setReconcileSuccess(null)
    try {
      const result = await reconcileReservePayTransaction({
        transactionId: tx.id,
        checkId: reconcilePreview.checkId,
        reason: reconcileReason,
        expectedLocalStatus: tx.status,
      })
      setReconcileSuccess(`ดำเนินการสำเร็จ: ${result.transaction.status}`)
      await handleVerify()
      onChanged()
    } catch (err: unknown) {
      setReconcileError(
        err instanceof Error
          ? err.message
          : (err as any)?.response?.data?.error || 'ไม่สามารถ reconcile ได้'
      )
    } finally {
      setReconciling(false)
    }
  }

  const rows = [
    ['หมายเลขอ้างอิง', tx.reference_number],
    ['ประเภท', tx.transaction_type === 'package_purchase' ? 'ซื้อแพ็กเกจ' : 'เติมเครดิต'],
    ['จำนวนเงิน', formatCurrency(tx.amount)],
    tx.credit_amount ? ['เครดิต', `${tx.credit_amount.toLocaleString()} เครดิต`] : null,
    tx.package_display_name ? ['แพ็กเกจ', tx.package_display_name] : null,
    ['สถานะ', tx.status],
    ['ลูกค้า', tx.customer_email ?? '-'],
    ['ธุรกิจ', tx.business_name ?? '-'],
    ['Package Level', tx.package_level ?? '-'],
    ['Flow', getFlowLabel(tx)],
    tx.metadata?.activation_choice ? ['Activation', tx.metadata.activation_choice] : null,
    ['Provider Ref (payment_id)', tx.external_reference ?? '-'],
    ['สร้างเมื่อ', formatDate(tx.created_at)],
    ['อัปเดตเมื่อ', formatDate(tx.updated_at)],
  ].filter(Boolean) as [string, string][]

  return (
    <Dialog open onOpenChange={() => onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-green-600" />
            รายละเอียด ReservePay Transaction
          </DialogTitle>
          <DialogDescription>{tx.id}</DialogDescription>
        </DialogHeader>

        <div className="space-y-2 text-sm mt-2">
          {rows.map(([label, value]) => (
            <div key={label} className="grid grid-cols-3 gap-2">
              <span className="text-muted-foreground">{label}:</span>
              <span className="col-span-2 font-medium break-all">{value}</span>
            </div>
          ))}
        </div>

        {/* Cross-check section */}
        <div className="mt-4 border-t pt-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm flex items-center gap-2">
              <RefreshCw className="w-4 h-4" />
              ตรวจสอบกับ ReservePay
            </h3>
            <Button
              size="sm"
              variant="outline"
              onClick={handleVerify}
              disabled={verifying}
              className="flex items-center gap-1"
            >
              {verifying ? (
                <><RefreshCw className="w-3 h-3 animate-spin" /> กำลังตรวจสอบ...</>
              ) : (
                <><RefreshCw className="w-3 h-3" /> ตรวจสอบ Live</>
              )}
            </Button>
          </div>

          {verifyError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              ❌ {verifyError}
            </div>
          )}

          {crossCheck && (
            <div className="space-y-3">
              <div className="grid gap-2 md:grid-cols-2 text-sm">
                <div className="p-3 bg-gray-50 rounded-lg border">
                  <div className="text-muted-foreground text-xs mb-1">Operational Status</div>
                  <div>{getOperationalBadge(crossCheck.operationalStatus, crossCheck.operationalStatus !== 'OK')}</div>
                </div>
                <div className="p-3 bg-gray-50 rounded-lg border">
                  <div className="text-muted-foreground text-xs mb-1">Webhook Health</div>
                  <div className="font-medium">{crossCheck.webhookHealth}</div>
                </div>
              </div>

              {/* Overall status */}
              <div className={`p-3 rounded-lg border text-sm font-medium flex items-center gap-2 ${
                crossCheck.crossCheckStatus === 'OK'
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : crossCheck.crossCheckStatus === 'PROVIDER_UNREACHABLE'
                  ? 'bg-yellow-50 border-yellow-200 text-yellow-800'
                  : 'bg-red-50 border-red-200 text-red-800'
              }`}>
                {crossCheck.crossCheckStatus === 'OK' && <><CheckCircle className="w-4 h-4" /> ไม่พบความผิดปกติ</>}
                {crossCheck.crossCheckStatus === 'PROVIDER_UNREACHABLE' && <><AlertCircle className="w-4 h-4" /> {crossCheck.providerError}</>}
                {crossCheck.crossCheckStatus === 'DISCREPANCY_FOUND' && <><AlertTriangle className="w-4 h-4" /> พบความไม่สอดคล้อง — ตรวจสอบด้านล่าง</>}
              </div>

              {/* Discrepancies */}
              {crossCheck.discrepancies.length > 0 && (
                <div className="border border-red-200 rounded-lg overflow-hidden">
                  <div className="bg-red-50 px-3 py-2 text-sm font-semibold text-red-800 flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" /> ความไม่สอดคล้อง ({crossCheck.discrepancies.length} รายการ)
                  </div>
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-1.5">ฟิลด์</th>
                        <th className="text-left px-3 py-1.5">ค่าของเรา</th>
                        <th className="text-left px-3 py-1.5">ค่า ReservePay</th>
                        <th className="text-left px-3 py-1.5">หมายเหตุ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {crossCheck.discrepancies.map((d, i) => (
                        <tr key={i} className="border-t border-red-100">
                          <td className="px-3 py-1.5 font-mono font-bold text-red-700">{d.field}</td>
                          <td className="px-3 py-1.5">{String(d.local)}</td>
                          <td className="px-3 py-1.5">{String(d.provider)}</td>
                          <td className="px-3 py-1.5 text-muted-foreground">{d.note}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Provider raw data */}
              {crossCheck.provider && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground py-1">
                    ข้อมูลดิบจาก ReservePay API
                  </summary>
                  <pre className="mt-2 p-3 bg-gray-50 rounded border text-xs overflow-x-auto">
                    {JSON.stringify(crossCheck.provider, null, 2)}
                  </pre>
                </details>
              )}

              <div className="border rounded-lg p-3 bg-slate-50">
                <div className="flex items-center justify-between gap-2 mb-3">
                  <h4 className="font-semibold text-sm">Manual Reconcile</h4>
                  {crossCheck.recommendedActions.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      แนะนำ: {crossCheck.recommendedActions.join(', ')}
                    </span>
                  )}
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium">เหตุผล</label>
                    <Textarea
                      className="mt-1 min-h-[84px]"
                      placeholder="เช่น Webhook signature invalid แต่ ReservePay ตอบ SUCCESSFUL"
                      value={reconcileReason}
                      onChange={(e) => setReconcileReason(e.target.value)}
                      disabled={previewing || reconciling}
                    />
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      onClick={handlePreviewReconcile}
                      disabled={!crossCheck || previewing || reconciling || reconcileReason.trim().length < 5}
                    >
                      {previewing ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> กำลัง preview...</> : 'Preview Reconcile'}
                    </Button>
                    <Button
                      onClick={handleExecuteReconcile}
                      disabled={!reconcilePreview?.allowed || reconciling}
                    >
                      {reconciling ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> กำลัง reconcile...</> : 'Execute Reconcile'}
                    </Button>
                  </div>

                  {reconcilePreview && (
                    <div className={`p-3 rounded-lg border text-sm ${reconcilePreview.allowed ? 'bg-green-50 border-green-200 text-green-800' : 'bg-yellow-50 border-yellow-200 text-yellow-900'}`}>
                      <div className="font-medium">Action: {reconcilePreview.actionType}</div>
                      <div className="mt-1">Provider status: {reconcilePreview.providerStatusRaw} ({reconcilePreview.providerStatusMapped})</div>
                      {!reconcilePreview.allowed && reconcilePreview.blockingReasons.length > 0 && (
                        <div className="mt-2">Blocked: {reconcilePreview.blockingReasons.join(', ')}</div>
                      )}
                    </div>
                  )}

                  {reconcileError && (
                    <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                      ❌ {reconcileError}
                    </div>
                  )}

                  {reconcileSuccess && (
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                      ✅ {reconcileSuccess}
                    </div>
                  )}
                </div>
              </div>

              {/* Webhook events */}
              {crossCheck.webhookEvents.length > 0 && (
                <details className="text-xs">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground py-1">
                    Webhook Events ที่ได้รับ ({crossCheck.webhookEvents.length} รายการ)
                  </summary>
                  <div className="mt-2 space-y-1">
                    {crossCheck.webhookEvents.map((ev, i) => (
                      <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 rounded border text-xs">
                        <Badge variant="outline" className="text-xs">{ev.event_name}</Badge>
                        <span className={ev.processing_status === 'processed' ? 'text-green-600' : 'text-yellow-600'}>{ev.processing_status}</span>
                        <span className="text-muted-foreground">{ev.provider_payment_id ?? '-'}</span>
                        <span className="ml-auto text-muted-foreground">{formatDate(ev.received_at)}</span>
                      </div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>

        <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200 text-sm text-green-800">
          ✅ รายการนี้ยืนยันอัตโนมัติผ่าน ReservePay webhook — ไม่ต้อง approve เอง
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─── refund dialog ────────────────────────────────────────────────────────────

function RefundDialog({
  tx,
  onClose,
  onRefunded,
}: {
  tx: AdminReservePayTransaction
  onClose: () => void
  onRefunded: () => void
}) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [reason, setReason] = useState('')

  // Check refund window
  const createdAt = new Date(tx.created_at)
  const deadline = new Date(createdAt.getTime() + 7 * 24 * 60 * 60 * 1000)
  const inWindow = new Date() <= deadline

  const isCredit = tx.transaction_type === 'credit_topup'

  async function handleRefund() {
    setLoading(true)
    setError(null)
    try {
      await refundReservePayTransaction({
        transactionId: tx.id,
        reason: reason || undefined,
      })
      onRefunded()
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : (err as any)?.response?.data?.error || 'เกิดข้อผิดพลาดในการคืนเงิน'
      setError(msg)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Undo2 className="w-5 h-5 text-orange-500" />
            ยืนยันการคืนเงิน
          </DialogTitle>
          <DialogDescription>
            การคืนเงินจะดำเนินการผ่าน ReservePay และไม่สามารถยกเลิกได้
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 gap-2 p-3 bg-gray-50 rounded-lg">
            <span className="text-muted-foreground">หมายเลขอ้างอิง:</span>
            <span className="font-mono font-medium">{tx.reference_number}</span>
            <span className="text-muted-foreground">จำนวนเงิน:</span>
            <span className="font-bold text-red-600">{formatCurrency(tx.amount)}</span>
            <span className="text-muted-foreground">ประเภท:</span>
            <span>{isCredit ? 'เติมเครดิต' : 'ซื้อแพ็กเกจ'}</span>
            {isCredit && tx.credit_amount && (
              <>
                <span className="text-muted-foreground">จะถูกหักเครดิต:</span>
                <span className="font-bold text-red-600">-{tx.credit_amount.toLocaleString()} เครดิต</span>
              </>
            )}
            {!isCredit && tx.package_display_name && (
              <>
                <span className="text-muted-foreground">จะยกเลิกแพ็กเกจ:</span>
                <span className="font-bold text-red-600">{tx.package_display_name}</span>
              </>
            )}
          </div>

          {!inWindow && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg border border-red-200 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>เกินกำหนดระยะเวลาคืนเงิน (7 วัน) — ไม่สามารถคืนเงินรายการนี้ได้</span>
            </div>
          )}

          <div>
            <label className="text-sm font-medium">เหตุผลการคืนเงิน (ไม่จำเป็น)</label>
            <input
              type="text"
              className="w-full mt-1 px-3 py-2 border rounded-md text-sm"
              placeholder="ระบุเหตุผล..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm border border-red-200">
              {error}
            </div>
          )}
        </div>

        <div className="flex gap-3 justify-end mt-2">
          <Button variant="outline" onClick={onClose} disabled={loading}>
            ยกเลิก
          </Button>
          <Button
            variant="destructive"
            onClick={handleRefund}
            disabled={loading || !inWindow}
          >
            {loading ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> กำลังคืนเงิน...</>
            ) : (
              <><Undo2 className="w-4 h-4 mr-2" /> ยืนยันการคืนเงิน ฿{tx.amount.toLocaleString()}</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ─── main component ──────────────────────────────────────────────────────────

export function AdminReservePayTransactions() {
  const [page, setPage] = useState(1)
  const [statusFilter, setStatusFilter] = useState<AdminReservePayTransactionsFilters['status'] | 'all'>('all')
  const [typeFilter, setTypeFilter] = useState<AdminReservePayTransactionsFilters['type'] | 'all'>('all')
  const [selectedTx, setSelectedTx] = useState<AdminReservePayTransaction | null>(null)
  const [refundTx, setRefundTx] = useState<AdminReservePayTransaction | null>(null)
  const pageSize = 20

  const { data, isLoading, error, refetch } = useAdminReservePayTransactions({
    page,
    limit: pageSize,
    status: statusFilter !== 'all' ? statusFilter : undefined,
    type: typeFilter !== 'all' ? typeFilter : undefined,
  })

  const completedCount = data?.transactions.filter((t) => t.status === 'completed').length ?? 0
  const totalRevenue = data?.transactions
    .filter((t) => t.status === 'completed')
    .reduce((sum, t) => sum + (Number(t.amount) || 0), 0) ?? 0

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-muted-foreground">กำลังโหลด...</div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-destructive">เกิดข้อผิดพลาด: {(error as Error).message}</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <Zap className="w-7 h-7 text-green-600" />
          ReservePay Transactions
        </h1>
        <p className="text-muted-foreground mt-1">
          รายการชำระเงิน PromptPay อัตโนมัติ — ยืนยันผ่าน webhook โดยไม่ต้อง approve เอง
        </p>
      </div>

      {/* Quick stats */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">รายการทั้งหมด (หน้านี้)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{data?.pagination.total ?? 0}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">สำเร็จ (หน้านี้)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{completedCount}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">รายได้ (หน้านี้)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{formatCurrency(totalRevenue)}</div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <CardTitle>รายการทั้งหมด</CardTitle>
              <CardDescription>
                แสดง {data?.transactions.length ?? 0} จาก {data?.pagination.total ?? 0} รายการ
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Select
                value={statusFilter}
                onValueChange={(v) => { setStatusFilter(v as any); setPage(1) }}
              >
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="สถานะ" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทุกสถานะ</SelectItem>
                  <SelectItem value="completed">สำเร็จ</SelectItem>
                  <SelectItem value="created">รอดำเนินการ</SelectItem>
                  <SelectItem value="pending">กำลังดำเนินการ</SelectItem>
                  <SelectItem value="failed">ล้มเหลว</SelectItem>
                  <SelectItem value="expired">หมดอายุ</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={typeFilter}
                onValueChange={(v) => { setTypeFilter(v as any); setPage(1) }}
              >
                <SelectTrigger className="w-36">
                  <SelectValue placeholder="ประเภท" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">ทุกประเภท</SelectItem>
                  <SelectItem value="credit_topup">เติมเครดิต</SelectItem>
                  <SelectItem value="package_purchase">ซื้อแพ็กเกจ</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>เวลา</TableHead>
                <TableHead>หมายเลขอ้างอิง</TableHead>
                <TableHead>ลูกค้า</TableHead>
                <TableHead>ประเภท</TableHead>
                <TableHead>วิธีชำระเงิน</TableHead>
                <TableHead>จำนวนเงิน</TableHead>
                <TableHead>Flow</TableHead>
                <TableHead>สถานะ</TableHead>
                <TableHead>Operational</TableHead>
                <TableHead className="text-right">รายละเอียด</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.transactions.map((tx) => (
                <TableRow key={tx.id}>
                  <TableCell className="whitespace-nowrap text-sm">{formatDate(tx.created_at)}</TableCell>
                  <TableCell>
                    <div className="font-medium text-sm">{tx.reference_number}</div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm">{tx.business_name ?? tx.customer_email ?? '-'}</div>
                    {tx.business_name && (
                      <div className="text-xs text-muted-foreground">{tx.customer_email}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    {getTypeBadge(tx.transaction_type)}
                    {tx.package_display_name && (
                      <div className="text-xs text-muted-foreground mt-0.5">{tx.package_display_name}</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`text-sm font-medium ${getMethodLabel(tx).color}`}>
                      {getMethodLabel(tx).label}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium">{formatCurrency(tx.amount)}</div>
                    {tx.credit_amount && (
                      <div className="text-xs text-muted-foreground">{tx.credit_amount.toLocaleString()} เครดิต</div>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground">{getFlowLabel(tx)}</span>
                  </TableCell>
                  <TableCell>{getStatusBadge(tx.status)}</TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      {getOperationalBadge(tx.operational_status, tx.needs_attention)}
                      {tx.last_provider_check_at && (
                        <div className="text-[11px] text-muted-foreground">{formatDate(tx.last_provider_check_at)}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      {tx.status === 'completed' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-orange-500 hover:text-orange-700 hover:bg-orange-50"
                          onClick={(e) => { e.stopPropagation(); setRefundTx(tx) }}
                        >
                          <Undo2 className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => setSelectedTx(tx)}>
                        <Eye className="w-4 h-4 mr-1" />
                        ดู
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {data?.transactions.length === 0 && (
                <TableRow>
                  <TableCell colSpan={10} className="text-center text-muted-foreground py-8">
                    ไม่พบรายการ
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          {data && data.pagination.total > pageSize && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-muted-foreground">
                หน้า {page} / {data.pagination.pages}
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                  ก่อนหน้า
                </Button>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => p + 1)} disabled={!data.pagination.hasMore}>
                  ถัดไป
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {selectedTx && <DetailModal tx={selectedTx} onClose={() => setSelectedTx(null)} onChanged={() => { void refetch() }} />}
      {refundTx && (
        <RefundDialog
          tx={refundTx}
          onClose={() => setRefundTx(null)}
          onRefunded={() => { setRefundTx(null); refetch() }}
        />
      )}
    </div>
  )
}

export default AdminReservePayTransactions
